import { useCallback, useEffect, useRef, useState } from 'react';
import type { OktaAuth } from '@okta/okta-auth-js';
import { SplunkConfig } from '@resolutionlife/ui';

import env from './env';

interface SplunkConfigApiResponse {
  hecUrl: string | null;
  tokenConfigured: boolean;
  index: string | null;
  sourceType: string | null;
  source: string | null;
}

interface SplunkTokenApiResponse {
  token: string | null;
}

const SPLUNK_ENABLED_ENVS = ['development', 'production'];
const currentEnv = process.env.REACT_APP_ENV || 'development';
const isSplunkEnabled = SPLUNK_ENABLED_ENVS.includes(currentEnv);

/**
 * Fetches Splunk configuration from the backend API at runtime using DPoP auth.
 */
async function fetchSplunkConfig(oktaAuth: OktaAuth): Promise<SplunkConfig> {
  const baseUrl = env.api.host;
  const configUrl = `${baseUrl}/splunk-config`;
  const tokenUrl = `${baseUrl}/splunk-config/token`;

  const tokens = await oktaAuth.tokenManager.getTokens();
  const accessToken = tokens?.accessToken;

  if (!accessToken) {
    throw new Error('No access token available');
  }

  // Generate DPoP headers for each request
  const [configHeaders, tokenHeaders] = await Promise.all([
    oktaAuth.getDPoPAuthorizationHeaders({ url: configUrl, method: 'GET', accessToken }),
    oktaAuth.getDPoPAuthorizationHeaders({ url: tokenUrl, method: 'GET', accessToken }),
  ]);

  const [configResponse, tokenResponse] = await Promise.all([
    fetch(configUrl, { headers: { ...configHeaders } }),
    fetch(tokenUrl, { headers: { ...tokenHeaders } }),
  ]);

  if (!configResponse.ok || !tokenResponse.ok) {
    throw new Error(`Failed to fetch Splunk config: config=${configResponse.status}, token=${tokenResponse.status}`);
  }

  const config: SplunkConfigApiResponse = await configResponse.json();
  const tokenData: SplunkTokenApiResponse = await tokenResponse.json();

  return {
    endpoint: `${baseUrl}/splunk-hec`,
    token: tokenData.token || '',
    source: config.source || 'cedant-ui',
    sourcetype: config.sourceType || 'ui_log',
    index: config.index || '',
  };
}

/** Empty config used as fallback when Splunk is disabled or not yet loaded. */
const EMPTY_CONFIG: SplunkConfig = {
  endpoint: '',
  token: '',
  source: 'cedant-ui',
  sourcetype: 'ui_log',
  index: '',
};

/**
 * React hook that fetches Splunk config from the backend once the user is authenticated.
 * Uses DPoP authentication matching the GraphQL provider pattern.
 */
export function useSplunkConfig(oktaAuth: OktaAuth | undefined): SplunkConfig {
  const [config, setConfig] = useState<SplunkConfig>(EMPTY_CONFIG);
  const fetchedRef = useRef(false);

  const loadConfig = useCallback(async () => {
    if (!isSplunkEnabled || fetchedRef.current || !oktaAuth) return;

    const accessToken = oktaAuth.getAccessToken();
    if (!accessToken) return;

    fetchedRef.current = true;

    try {
      const splunkConfig = await fetchSplunkConfig(oktaAuth);
      setConfig(splunkConfig);
    } catch (error) {
      console.warn('[useSplunkConfig] Failed to fetch Splunk config from backend:', error);
    }
  }, [oktaAuth]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return config;
}

export default EMPTY_CONFIG;
